import React, { Dispatch, SetStateAction } from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Card } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Input } from '@lightspeed/flame/Input';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import { SaleManager } from 'payments/PaymentDepositModal/types';

import {
  fetchCustomerId,
  fetchPrimaryCustomerEmail,
  updateAccountEmailRequest,
} from '../../CreditAccountStatement/utils';
import { ADD_PAYMENT_DEPOSIT_MODAL_ERRORS, DisplayModalValues } from './AddPaymentDepositModal';

const EmailPaymentModal: React.FC<{
  hasLightSpeedPayments: boolean;
  isModalOpen: boolean;
  setModalView: Dispatch<SetStateAction<string>>;
  setPreviousModalView: Dispatch<SetStateAction<string>>;
  amount: string;
  saleManager: SaleManager;
  handleSubmitRemoteDepositPaymentRequest: (sendEmail?: boolean) => Promise<void>;
  isError: boolean;
}> = ({
  isModalOpen,
  setModalView,
  setPreviousModalView,
  amount,
  saleManager,
  handleSubmitRemoteDepositPaymentRequest,
  isError,
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [isUpdateToCustomerProfileRequired, setIsUpdateToCustomerProfileRequired] =
    React.useState(false);
  const [isErrorUpdatingCustomerEmail, setIsErrorUpdatingCustomerEmail] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [emailValidationError, setEmailValidationError] = React.useState(false);
  const [primaryEmail, setPrimaryEmail] = React.useState('');

  const { id: accountId } = window.merchantos.account;

  const hasCustomerProfileUpdateRights = !!window.merchantos.session.hasRight('customers');

  const validateEmail = () => {
    if (inputValue.length > 0 && window.isEmail(inputValue)) {
      setEmailValidationError(false);
      return true;
    }

    setEmailValidationError(true);
    return false;
  };

  React.useEffect((): void => {
    const fetchCustomerEmail = async () => {
      setIsLoading(true);

      const customerId = fetchCustomerId();

      // if the customer does not have a primary email we can populate this field with, we are going to force the update to the customer profile with the email entered in by the merchant otherwise the emailing service will fail on the BE
      if (!customerId) {
        setIsUpdateToCustomerProfileRequired(true);
        return;
      }
      const primaryEmail = await fetchPrimaryCustomerEmail(customerId);
      setInputValue(primaryEmail);
      setPrimaryEmail(primaryEmail);

      if (primaryEmail === '' || primaryEmail.length === 0 || !window.isEmail(primaryEmail)) {
        setIsUpdateToCustomerProfileRequired(true);
      }
      setIsLoading(false);
    };

    fetchCustomerEmail();
  }, [accountId]);

  const handleSubmit = async () => {
    setIsLoading(true);
    setIsErrorUpdatingCustomerEmail(false);

    if (isUpdateToCustomerProfileRequired || primaryEmail !== inputValue) {
      const checkIsCustomerProfileUpdated = await handleUpdateEmailToCustomerProfile();

      if (!checkIsCustomerProfileUpdated) {
        setIsLoading(false);
        setIsErrorUpdatingCustomerEmail(true);
        return;
      }
    }

    await handleSubmitRemoteDepositPaymentRequest(true);
  };

  const handleUpdateEmailToCustomerProfile = async (): Promise<boolean> => {
    const customerId = fetchCustomerId();

    if (!customerId) {
      setIsErrorUpdatingCustomerEmail(true);
      return false;
    }
    if (await updateAccountEmailRequest(accountId, customerId, inputValue)) {
      return true;
    }
    setIsErrorUpdatingCustomerEmail(true);
    return false;
  };

  const handleModalComplete = (): void => {
    setPreviousModalView('add-payment-deposit-email');
  };

  const handleModalCancel = (): void => {
    setModalView('cancel-deposit');
    setPreviousModalView('add-payment-deposit-email');
  };

  return (
    <Modal isOpen={isModalOpen} onRequestClose={handleModalComplete} css={{ width: '531px' }}>
      <ModalHeader
        onCloseClicked={handleModalCancel}
        data-test="modal-header-customer-name"
        style={{ paddingTop: '8px', paddingBottom: '8px' }}
      >
        Send payment link by email
      </ModalHeader>
      <ModalBody style={{ padding: '18px' }}>
        <Flex flexDirection="column">
          {!hasCustomerProfileUpdateRights && !isUpdateToCustomerProfileRequired ? (
            <Alert type="warning" noCloseBtn mb={2} data-test="customer-rights-missing-valid-email">
              Your role does not have the required permissions to update the customer profile, the
              existing email address will be used.
            </Alert>
          ) : null}
          {!hasCustomerProfileUpdateRights && isUpdateToCustomerProfileRequired ? (
            <Alert
              type="warning"
              noCloseBtn
              mb={2}
              data-test="customer-rights-missing-invalid-email"
            >
              The customer does not have an existing valid email address and your role does not have
              the required permissions to update the customer profile. Select generate the payment
              link in the previous step, and copy and share where you like.
            </Alert>
          ) : null}
          {isErrorUpdatingCustomerEmail ||
            (isError && (
              <Alert
                type="danger"
                noCloseBtn
                data-test="error-alert"
                mb={2}
                title={
                  isErrorUpdatingCustomerEmail
                    ? ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_UPDATING_CUSTOMER_EMAIL.title
                    : ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_ADDING_IN_PERSON_DEPOSIT.title
                }
              >
                {isErrorUpdatingCustomerEmail
                  ? ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_UPDATING_CUSTOMER_EMAIL.message
                  : ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_ADDING_IN_PERSON_DEPOSIT.message}
              </Alert>
            ))}
          <Card p={3} mb={3}>
            {isLoading ? (
              <Box>
                <Bone height={3} width={10} style={{ marginBottom: '10px' }} />
                <Bone height={10} />
              </Box>
            ) : (
              <>
                <Text fontSize="16px" fontWeight="bold" mb="10px" className="notranslate">
                  {saleManager.customer.fullName}
                </Text>
                <Box>
                  <Flex flexWrap="wrap">
                    <Box>
                      <DisplayModalValues
                        title="Deposit amount requested"
                        total={Number(amount)}
                        testID="deposit-amount-requested"
                      />
                    </Box>
                  </Flex>
                </Box>
              </>
            )}
          </Card>
          <Box>
            {isLoading ? (
              <Flex flexDirection="column" data-test="loading-wrapper">
                <Box width="100%">
                  <Bone height={4} />
                </Box>
                <Box width="100%" mt={2}>
                  <Bone height={4} />
                </Box>
              </Flex>
            ) : (
              <>
                <Input
                  type="email"
                  placeholder="Enter a recipient email"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onBlur={() => validateEmail()}
                  style={{ paddingLeft: '12px', paddingRight: '12px', borderRadius: '3px' }}
                  autoFocus
                  autoComplete="off"
                  data-test="email-input"
                  status={emailValidationError ? 'error' : undefined}
                  disabled={!hasCustomerProfileUpdateRights}
                />
                {emailValidationError && (
                  <Text size="small" mt={1} ml={1} color="#DE2E37">
                    A valid email address is required.
                  </Text>
                )}
                {!emailValidationError && primaryEmail !== inputValue && (
                  <Text size="small" mt={1}>
                    Email address will be updated on customer profile.
                  </Text>
                )}
              </>
            )}
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="flex-end">
          <Button
            mr={2}
            onClick={() => setModalView('request-deposit-paylink')}
            data-test="cancel-button"
            disabled={isLoading}
          >
            Back
          </Button>
          <Button
            disabled={
              isLoading || !inputValue || emailValidationError || !hasCustomerProfileUpdateRights
            }
            variant="secondary"
            fill
            data-test="send-button"
            onClick={handleSubmit}
          >
            Done
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default EmailPaymentModal;
